import { getPolicyRuleIconSymbol } from "./policyRuleIcons";

export interface PolicyRuleInput {
  title: string;
  description: string;
  /** Icons toggle from the dialog. When on and `iconId` is known, the icon symbol leads the heading. */
  withIcon: boolean;
  iconId?: string;
}

export interface PolicyRuleErrors {
  title?: boolean;
  description?: boolean;
}

export function validatePolicyRule(
  rule: Pick<PolicyRuleInput, "title" | "description">,
): PolicyRuleErrors {
  const errors: PolicyRuleErrors = {};
  if (!rule.title.trim()) errors.title = true;
  if (!rule.description.trim()) errors.description = true;
  return errors;
}

const MARKDOWN_SPECIAL = new Set([..."\\*_`[]<>#|~"]);

function escapeLine(line: string): string {
  const escaped = [...line]
    .map((ch) => (MARKDOWN_SPECIAL.has(ch) ? `\\${ch}` : ch))
    .join("");
  return escaped.startsWith("-") ? `\\${escaped}` : escaped;
}

export function escapeMarkdown(value: string): string {
  return value
    .split("\n")
    .map((line) => escapeLine(line.trim()))
    .join("\n");
}

const singleLine = (value: string) =>
  value
    .split("\n")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");

/** One Markdown section: `## [symbol ]Title\nDescription`. */
export function formatPolicyRule(rule: PolicyRuleInput): string {
  const title = escapeMarkdown(singleLine(rule.title));
  const description = escapeMarkdown(rule.description.trim());
  const symbol = rule.withIcon
    ? getPolicyRuleIconSymbol(rule.iconId)
    : undefined;
  const heading = symbol ? `${symbol} ${title}` : title;
  return `## ${heading}\n${description}`;
}

export function appendPolicyRule(
  policyText: string | undefined,
  rule: PolicyRuleInput,
): string {
  const section = formatPolicyRule(rule);
  const current = (policyText ?? "").trimEnd();
  return current ? `${current}\n\n${section}` : section;
}
